import { Controller } from "@hotwired/stimulus"

// Simple dropdown controller for toggling menus.
//
// aria-expanded: the trigger is the only thing that can carry it (the popup
// itself is what is expanded, not the announcer of it), and it has to be
// updated from EVERY path that changes visibility — including clickOutside,
// which is how these menus are usually closed. Opt in by marking the trigger
// `data-dropdown-target="trigger"` and rendering aria-expanded="false" on it;
// the attribute is only written when that target exists, so the dropdowns that
// have not been given one behave exactly as before.
export default class extends Controller {
  static targets = ["menu", "trigger"]

  connect() {
    // Close dropdown when clicking outside
    this.boundClickOutside = this.clickOutside.bind(this)
    document.addEventListener("click", this.boundClickOutside)
    // Re-assert the attribute from the DOM rather than trusting the server:
    // Turbo can restore a cached page with the menu already open.
    this.syncExpanded()
  }

  disconnect() {
    document.removeEventListener("click", this.boundClickOutside)
  }

  toggle(event) {
    event.stopPropagation()

    if (this.hasMenuTarget) {
      this.menuTarget.classList.toggle("hidden")
      this.syncExpanded()
    }
  }

  show() {
    if (this.hasMenuTarget) {
      this.menuTarget.classList.remove("hidden")
      this.syncExpanded()
    }
  }

  hide() {
    if (this.hasMenuTarget) {
      this.menuTarget.classList.add("hidden")
      this.syncExpanded()
    }
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hide()
    }
  }

  // Mirrors the menu's own visibility, so there is one source of truth: the
  // `hidden` class this controller toggles.
  syncExpanded() {
    if (!this.hasTriggerTarget || !this.hasMenuTarget) return
    const open = !this.menuTarget.classList.contains("hidden")
    this.triggerTarget.setAttribute("aria-expanded", String(open))
  }
}
